import React, { useMemo, useEffect } from 'react';
import { Spin, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { AssistantChatbotPreview } from './AssistantChatbotPreview';
import { IFlowQuestion } from '../../utils/jsonFlowGenerator';

interface ConfigurationViewTabProps {
  questions: IFlowQuestion[];
  backendConfig?: any;
  loading?: boolean;
}

export const ConfigurationViewTab: React.FC<ConfigurationViewTabProps> = ({
  questions,
  backendConfig,
  loading = false,
}) => {
  const [chatbotKey, setChatbotKey] = React.useState(0);

  // Reset chatbot whenever questions change (config updated in editor tab)
  useEffect(() => {
    setChatbotKey(prev => prev + 1);
  }, [questions]);

  const conversationFlow = useMemo(() => ({
    version: backendConfig?.Version || '1.0.0',
    chatbotConfig: {
      greeting: backendConfig?.Config?.chatbotConfig?.greeting || "Hello! I'm here to help you configure your perfect window.",
      language: backendConfig?.Config?.chatbotConfig?.language || 'en-GB',
      currency: backendConfig?.Config?.chatbotConfig?.currency || 'GBP',
      units: backendConfig?.Config?.chatbotConfig?.units || 'metric',
      defaultRegion: backendConfig?.Config?.chatbotConfig?.defaultRegion || 'UK',
    },
    questionFlow: questions,
    validationRules: backendConfig?.Config?.validationRules || [],
    pricingRules: backendConfig?.Config?.pricingRules || [],
  }), [questions, backendConfig]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spin size="large" tip="Loading configuration..." />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-text-secondary">
          No configuration data available. Please add categories and options in the Configuration Editor tab.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg">
      <div className="mb-4 flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Configuration Preview</h3>
          <p className="text-sm text-text-secondary">
            Preview and test the chatbot configuration with live data from your setup.
          </p>
        </div>
        <Button
          icon={<DeleteOutlined />}
          onClick={() => setChatbotKey(prev => prev + 1)}
          danger
        >
          Clear History
        </Button>
      </div>

      <AssistantChatbotPreview
        conversationFlow={conversationFlow}
        chatbotKey={chatbotKey}
      />
    </div>
  );
};
